/**
 * OrderService - Handles order-related operations
 * 
 * This service provides methods to fetch completed order details
 * from the backend, which in turn queries Dutchie's API.
 */

export interface OrderCustomer {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

export interface OrderItem {
  option: string;
  price: number;
  quantity: number;
  subtotal: number;
  productId: string;
  product?: {
    id: string;
    name: string;
    category: string;
    subcategory?: string;
    strainType?: string;
    description?: string;
    images?: Array<{
      id: string;
      url: string;
      label?: string;
      description?: string;
    }>;
    brand?: {
      id: string;
      name: string;
      description?: string;
      imageUrl?: string;
    };
  };
}

export interface OrderTaxes {
  cannabis?: number;
  sales?: number;
}

export interface OrderFees {
  delivery?: number;
  processing?: number;
  excise?: number;
}

export interface OrderDiscounts {
  total?: number;
}

export interface OrderDetails {
  id: string;
  orderNumber: string;
  status: string;
  createdAt: string;
  dispensaryName: string;
  customerId?: string;
  customer?: OrderCustomer;
  delivery: boolean;
  pickup: boolean;
  medical: boolean;
  recreational: boolean;
  deliveryAddress?: {
    ln1?: string;
    ln2?: string;
    city?: string;
    state?: string;
    zipcode?: string;
  };
  items: OrderItem[];
  subtotal: number;
  total: number;
  taxes?: OrderTaxes;
  fees?: OrderFees;
  discounts?: OrderDiscounts;
  paymentMethod: string;
  foreignId: string;
  metadata?: any;
}

export interface OrderResponse {
  success: boolean;
  data?: OrderDetails;
  error?: string;
}

export class OrderService {
  private static baseUrl = `${process.env.REACT_APP_BACKEND_URL!}/api/checkout`;

  /**
   * Smart order lookup - finds order by order number in recent orders
   * This is the preferred method as it works around API permission issues
   * 
   * @param retailerId - The retailer ID 
   * @param orderNumber - The order number returned by Dutchie
   * @param searchLimit - Number of recent orders to search through (optional)
   * @returns Promise resolving to order details
   * @throws Error if order fetch fails
   */
  public static async findOrderByNumber(
    retailerId: string,
    orderNumber: string,
    searchLimit: number = 100
  ): Promise<OrderDetails> {
    try {
      const url = `${this.baseUrl}/orders/find/${retailerId}/${orderNumber}${searchLimit !== 100 ? `?searchLimit=${searchLimit}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success || !data.data?.order) {
        throw new Error(data.error || 'Failed to retrieve order details');
      }

      return data.data.order;
    } catch (error) {
      console.error('Error finding order by number:', error);
      throw error;
    }
  }

  /**
   * Fetches detailed order information by order ID (original method)
   * 
   * @param retailerId - The retailer ID 
   * @param orderId - The order ID returned by Dutchie
   * @returns Promise resolving to order details
   * @throws Error if order fetch fails
   */
  public static async getOrderDetails(retailerId: string, orderId: string): Promise<OrderDetails> {
    try {
      const response = await fetch(`${this.baseUrl}/order/${retailerId}/${orderId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data: OrderResponse = await response.json();
      
      if (!data.success || !data.data) {
        throw new Error(data.error || 'Failed to retrieve order details');
      }

      return data.data;
    } catch (error) {
      console.error('Error retrieving order details:', error);
      throw error;
    }
  }

  /**
   * Attempts to get order details with multiple retailer fallbacks using smart lookup
   * This is useful when we don't know which retailer was used for the order
   * 
   * @param orderNumber - The order number returned by Dutchie
   * @param retailerIds - Array of retailer IDs to try (optional)
   * @param searchLimit - Number of recent orders to search through (optional)
   * @returns Promise resolving to order details and successful retailer ID
   */
  public static async findOrderWithFallback(
    orderNumber: string,
    retailerIds?: string[],
    searchLimit: number = 100
  ): Promise<{ order: OrderDetails; retailerId: string }> {
    // Collect all retailer IDs from environment variables
    const defaultRetailerIds: string[] = [];
    const envKeys = Object.keys(process.env).filter(key => key.startsWith('REACT_APP_RETAILER_ID_'));
    
    for (const key of envKeys) {
      const retailerId = process.env[key];
      if (retailerId && !defaultRetailerIds.includes(retailerId)) {
        defaultRetailerIds.push(retailerId);
      }
    }
    
    // If no env vars found, fall back to known IDs
    if (defaultRetailerIds.length === 0) {
      defaultRetailerIds.push(
        '60e98181-4255-48d4-a3dd-8dde829d0620', // SweetSpot Olney
        '9fc171e5-b11a-4434-910f-08253f1a6aec'  // SweetSpot River Edge (fallback)
      );
    }

    const idsToTry = retailerIds || defaultRetailerIds;

    for (const retailerId of idsToTry) {
      try {
        const orderDetails = await this.findOrderByNumber(retailerId, orderNumber, searchLimit);
        return { order: orderDetails, retailerId };
      } catch (error) {
        // Continue to next retailer
      }
    }

    throw new Error(`Order ${orderNumber} not found with any of the available retailers`);
  }

  /**
   * Attempts to get order details with multiple retailer fallbacks (original method)
   * This is useful when we don't know which retailer was used for the order
   * 
   * @param orderId - The order ID returned by Dutchie
   * @param retailerIds - Array of retailer IDs to try (optional)
   * @returns Promise resolving to order details
   */
  public static async getOrderDetailsWithFallback(
    orderId: string,
    retailerIds?: string[]
  ): Promise<OrderDetails> {
    // Collect all retailer IDs from environment variables
    const defaultRetailerIds: string[] = [];
    const envKeys = Object.keys(process.env).filter(key => key.startsWith('REACT_APP_RETAILER_ID_'));

    for (const key of envKeys) {
      const retailerId = process.env[key];
      if (retailerId && !defaultRetailerIds.includes(retailerId)) {
        defaultRetailerIds.push(retailerId);
      }
    }

    // If no env vars found, fall back to known IDs
    if (defaultRetailerIds.length === 0) {
      defaultRetailerIds.push(
        '60e98181-4255-48d4-a3dd-8dde829d0620', // SweetSpot Olney
        '9fc171e5-b11a-4434-910f-08253f1a6aec'  // SweetSpot River Edge (fallback)
      );
    }

    const idsToTry = retailerIds || defaultRetailerIds;

    for (const retailerId of idsToTry) {
      try {
        const orderDetails = await this.getOrderDetails(retailerId, orderId);
        return orderDetails;
      } catch (error) {
        // Continue to next retailer
      }
    }

    throw new Error(`Order ${orderId} not found with any of the available retailers`);
  }

  /**
   * Formats order details for display purposes
   * 
   * @param order - Raw order details from API
   * @returns Formatted order data for UI display
   */
  public static formatOrderForDisplay(order: OrderDetails) {
    return {
      orderNumber: order.orderNumber || 'Unknown',
      status: order.status?.toLowerCase() || 'unknown',
      statusDisplay: this.formatOrderStatus(order.status || 'unknown'),
      date: order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'Unknown',
      time: order.createdAt ? new Date(order.createdAt).toLocaleTimeString() : 'Unknown',
      dispensary: order.dispensaryName,
      fulfillmentType: order.pickup ? 'Pickup' : order.delivery ? 'Delivery' : 'Unknown',
      pricingType: order.medical ? 'Medical' : order.recreational ? 'Recreational' : 'Unknown',
      customer: order.customer ? {
        name: [order.customer.firstName, order.customer.lastName].filter(Boolean).join(' '),
        email: order.customer.email,
        phone: order.customer.phone
      } : null,
      items: order.items?.map(item => ({
        name: item.product?.name || 'Unknown Product',
        brand: item.product?.brand?.name,
        option: item.option,
        quantity: item.quantity,
        price: item.price,
        subtotal: item.subtotal,
        image: item.product?.images?.[0]?.url
      })) || [],
      pricing: {
        subtotal: order.subtotal,
        taxes: (order.taxes?.cannabis || 0) + (order.taxes?.sales || 0),
        fees: (order.fees?.delivery || 0) + (order.fees?.processing || 0) + (order.fees?.excise || 0),
        discounts: order.discounts?.total || 0,
        total: order.total
      },
      deliveryAddress: order.delivery && order.deliveryAddress ? {
        line1: order.deliveryAddress.ln1,
        line2: order.deliveryAddress.ln2,
        city: order.deliveryAddress.city,
        state: order.deliveryAddress.state,
        zipcode: order.deliveryAddress.zipcode
      } : null
    };
  }

  /**
   * Formats order status for user-friendly display
   */
  private static formatOrderStatus(status: string): string {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'complete':
        return 'Completed';
      case 'pending':
        return 'Processing';
      case 'confirmed':
        return 'Confirmed';
      case 'ready':
        return 'Ready for Pickup';
      case 'cancelled':
      case 'canceled':
        return 'Cancelled';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    }
  }
}